import { useMemo, useRef, useState, useEffect } from 'react'
import { useDbValue } from '../lib/useDbValue.js'
import { askConcierge, buildConciergeContext, resolveCurrentEventId, CONCIERGE_CATEGORIES } from '../utils/concierge.js'

// Tournament Concierge — READ-ONLY player/spectator Q&A. Floating button on the
// phone player/spectator screens. Every answer is grounded in the live 4-Ball
// data the app computes (buildConciergeContext); the AI only phrases it. This is
// NOT the admin edit AI — it cannot change anything.
const GREEN = 'var(--ge-primary)'
const CREAM = 'var(--ge-cream-premium)'
const GOLD = 'var(--ge-gold)'

export default function Concierge({ playerName = null, fabTop = 'calc(8px + env(safe-area-inset-top))' }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ask AI"
          style={{
            // TOP-right so it never overlaps anything anchored at the bottom
            // (e.g. the persistent "Start Qualifying" pill). `fabTop` lets a host
            // page drop it lower — e.g. level with the hub's tall tree logo,
            // clear of the top-corner Photos pill.
            position: 'fixed', right: 12, top: fabTop, zIndex: 9998,
            display: 'flex', alignItems: 'center', gap: 6,
            background: GREEN, color: CREAM, border: `1.5px solid ${GOLD}`,
            borderRadius: 999, padding: '8px 14px', fontWeight: 800, fontSize: 14,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)', cursor: 'pointer',
          }}>
          <span style={{ fontSize: 18 }}>💬</span> Ask AI
        </button>
      )}
      {open && <ConciergePanel playerName={playerName} onClose={() => setOpen(false)} />}
    </>
  )
}

function ConciergePanel({ playerName, onClose }) {
  // Lazy — the heavy tournaments subscription only runs while the panel is open.
  const { data: all, status } = useDbValue('tournaments')
  const currentId = useMemo(() => resolveCurrentEventId(all || {}), [all])
  const tournament = currentId ? all?.[currentId] : null
  const context = useMemo(() => (tournament ? buildConciergeContext(tournament, {}) : null), [tournament])

  const name = (playerName || '').trim() || 'there'
  const [messages, setMessages] = useState([]) // { role: 'you'|'ai', text }
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef(null)

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' }) }, [messages, busy])

  const send = async (question, category) => {
    const q = (question || '').trim()
    if (!q || busy) return
    setMessages((m) => [...m, { role: 'you', text: q }])
    setInput('')
    setBusy(true)
    const ctx = context || { event: null, note: 'Live tournament data is still loading.' }
    const answer = await askConcierge(q, category || null, ctx, name === 'there' ? null : name)
    setMessages((m) => [...m, { role: 'ai', text: answer }])
    setBusy(false)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.35)' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -10px 40px rgba(0,0,0,0.4)' }}>
        {/* header */}
        <div style={{ background: GREEN, color: CREAM, padding: '14px 16px', borderTopLeftRadius: 20, borderTopRightRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, letterSpacing: 1, color: GOLD, fontWeight: 800, textTransform: 'uppercase' }}>Ask AI</div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>Hi {name}, what would you like to know?</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: CREAM, fontSize: 26, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>

        {/* category chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 16px 4px' }}>
          {CONCIERGE_CATEGORIES.map((c) => (
            <button key={c.key} type="button" disabled={busy} onClick={() => send(c.q, c.key)}
              style={{ background: CREAM, color: GREEN, border: `1.5px solid ${GREEN}`, borderRadius: 999, padding: '8px 12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>

        {/* thread */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', minHeight: 120 }}>
          {messages.length === 0 && (
            <p style={{ color: '#4B5563', fontSize: 14, lineHeight: 1.5 }}>
              Tap a category above or ask anything about the tournament — who's leading, a player's status, photos by hole, or the rules.
              I only look things up from the live scoreboard — I can't change anything.
            </p>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'you' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
              <div style={{
                maxWidth: '85%', padding: '9px 12px', borderRadius: 14, fontSize: 15, lineHeight: 1.45, whiteSpace: 'pre-wrap',
                background: m.role === 'you' ? GREEN : '#F1F5F2', color: m.role === 'you' ? CREAM : '#111827',
                borderBottomRightRadius: m.role === 'you' ? 4 : 14, borderBottomLeftRadius: m.role === 'you' ? 14 : 4,
              }}>{m.text}</div>
            </div>
          ))}
          {busy && <div style={{ color: '#6B7280', fontSize: 14, fontStyle: 'italic' }}>…checking the live scoreboard</div>}
          {status === 'error' && <div style={{ color: '#B91C1C', fontSize: 13 }}>Couldn't load the live data — answers may be limited.</div>}
        </div>

        {/* input */}
        <form onSubmit={(e) => { e.preventDefault(); send(input) }}
          style={{ display: 'flex', gap: 8, padding: '10px 16px calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid #E5E7EB' }}>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about the tournament…"
            style={{ flex: 1, border: '1.5px solid #CBD5E1', borderRadius: 999, padding: '11px 14px', fontSize: 16, outline: 'none' }} />
          <button type="submit" disabled={busy || !input.trim()}
            style={{ background: GREEN, color: CREAM, border: 'none', borderRadius: 999, padding: '0 18px', fontWeight: 800, fontSize: 15, cursor: 'pointer', opacity: (busy || !input.trim()) ? 0.6 : 1 }}>
            Ask
          </button>
        </form>
      </div>
    </div>
  )
}
